import { apiClient } from './api';
import {
  connectDisplay,
  disconnectDisplay,
  fetchDisplayStatus,
  fetchMyDisplayConnection,
  presentDisplayImage,
  presentDisplayMedia,
  presentDisplayText
} from './displayApi';

export interface TeachingUnitSummary {
  id: number;
  uid: string;
  title: string;
  series_name?: string | null;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  parent_type: 'class' | 'course' | 'group' | 'ad_hoc';
  parent_id: number | null;
  context_label?: string | null;
  subject_id: number | null;
  curriculum_id: number | null;
  primary_sitzplan_id: number | null;
  nfc_auto_enabled: boolean;
  doc_auto_enabled: boolean;
  start_at: string | null;
  end_at: string | null;
  created_by: number | null;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  participant_count: number;
  competency_count: number;
}

export interface TeachingUnitParticipant {
  teaching_unit_id: number;
  student_id: string;
  origin: 'class' | 'course' | 'group' | 'manual';
  origin_reference: string | null;
  is_active: number;
  created_at: string;
  first_name?: string | null;
  last_name?: string | null;
  klasse?: string | null;
  sophomorix_unid?: string | null;
}

export interface TeachingUnitCompetency {
  id: number;
  teaching_unit_id: number;
  competency_id: number;
  target_level_id: number | null;
  is_focus: number;
  notes: string | null;
  competency_name?: string;
  curriculum_id?: number;
}

export interface TeachingUnitIdea {
  id: number;
  idea_rank: number;
  idea_short: string;
  idea_detail: string | null;
  model: string;
  generated_at: string;
  generated_by: number | null;
}

export interface TeachingUnitDetail {
  unit: TeachingUnitSummary;
  participants: TeachingUnitParticipant[];
  competencies: TeachingUnitCompetency[];
  ideas: TeachingUnitIdea[];
}

export interface TeachingUnitCreatePayload {
  title: string;
  series_name?: string | null;
  status?: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  parent_type: 'class' | 'course' | 'group' | 'ad_hoc';
  parent_id?: number;
  subject_id?: number;
  curriculum_id?: number;
  primary_sitzplan_id?: number;
  nfc_auto_enabled?: boolean;
  doc_auto_enabled?: boolean;
  start_at?: string;
  end_at?: string;
  participants?: Array<{
    student_id: string;
    origin?: 'class' | 'course' | 'group' | 'manual';
    origin_reference?: string | null;
    is_active?: boolean;
  }>;
  competencies?: Array<{
    competency_id: number;
    target_level_id?: number | null;
    is_focus?: boolean;
    notes?: string | null;
  }>;
  additional_users?: Array<{
    user_id: number;
    role?: 'owner' | 'co_teacher' | 'observer';
  }>;
}

export interface TeachingUnitUpdatePayload extends Partial<TeachingUnitCreatePayload> {}

export interface TeachingPreparationMeta {
  classes: Array<{ id: string | number | null; class_name: string; student_count?: number | null }>;
  courses: Array<{ id: number; name: string; fach: string | null; jahrgang: string | null; is_active?: number | boolean; berechtigung?: string }>;
  groups: Array<{ id: number; name: string; gruppen_typ: string | null }>;
  subjects: Array<{ id: number; name: string; department: string | null }>;
  curricula: Array<{ id: number; name: string; subject: string | null }>;
  sitzplaene: Array<{
    id: number;
    name: string;
    klasse: string | null;
    context_type?: 'class' | 'course' | 'group' | null;
    context_value?: string | null;
    context_label?: string | null;
  }>;
}

export interface QuickstartStudent {
  id: string;
  sophomorix_unid: string;
  first_name: string;
  last_name: string;
  class_name: string;
}

export interface QuickstartSitzplanPosition {
  grid_row: number;
  grid_col: number;
  schueler_id: number | null;
  sophomorix_unid: string | null;
  first_name: string | null;
  last_name: string | null;
  class_name: string | null;
}

export interface QuickstartSitzplanSuggestion {
  id: number;
  name: string;
  klasse: string | null;
  grid_rows: number;
  grid_cols: number;
  updated_at: string;
  total_positions: number;
  class_match_positions: number;
  coverage: number;
  positionen: QuickstartSitzplanPosition[];
}

export interface QuickstartCompetencySuggestion {
  id: number;
  name: string;
  description: string | null;
  curriculum_id: number;
  curriculum_name: string | null;
  score: number;
}

export interface QuickstartKeywordHint {
  keyword: string;
  explanation: string;
}

export interface QuickstartCurriculumMatch {
  id: number;
  name: string;
  subject: string | null;
  score: number;
}

export interface QuickstartContentFieldMatch {
  id: number;
  curriculum_id: number;
  curriculum_name: string | null;
  name: string;
  score: number;
}

export interface QuickstartImageResult {
  query: string;
  title: string;
  thumbnailUrl: string;
  imageUrl: string;
  sourcePageUrl: string;
  author: string;
  license: string;
  provider?: 'wikimedia' | 'google' | 'pixabay' | 'pexels' | 'unsplash';
}

export interface QuickstartImageGalleryGroup {
  query: string;
  images: QuickstartImageResult[];
}

export interface QuickstartImageSourceSettings {
  wikipedia: boolean;
  pixabay: boolean;
  pexels: boolean;
  unsplash: boolean;
  google: boolean;
}

export interface TeachingQuickstartContext {
  className: string;
  subject: { id: number; name: string | null } | null;
  theme: string;
  students: QuickstartStudent[];
  sitzplanSuggestions: QuickstartSitzplanSuggestion[];
  suggestedSitzplanId: number | null;
  competencySuggestions: QuickstartCompetencySuggestion[];
  allCurriculumCompetencies: QuickstartCompetencySuggestion[];
  defaultSelectedCompetencyIds: number[];
  keywordHints: QuickstartKeywordHint[];
  imageQueryHints: string[];
  imageGallery: QuickstartImageGalleryGroup[];
  imageSourceSettings?: QuickstartImageSourceSettings;
  curriculumMatches: QuickstartCurriculumMatch[];
  contentFieldMatches: QuickstartContentFieldMatch[];
}

export interface TeachingQuickstartSavedState {
  selectedCompetencyIds: number[];
  selectedKeywordHint: QuickstartKeywordHint | null;
  bookingCounts: Record<string, number>;
  keywordHints: QuickstartKeywordHint[];
  plannedMediaItems: Array<{
    id: string;
    type: 'media' | 'text';
    assetId?: number | null;
    title: string;
    imageUrl?: string;
    mediaUrl?: string;
    mediaType?: 'image' | 'video' | 'pdf' | 'youtube';
    mimeType?: string;
    sourcePageUrl?: string;
    originalName?: string;
    uploadedAt?: string | null;
    lastUsedAt?: string | null;
    usageCount?: number;
    text?: string;
    createdAt?: string;
  }>;
}

export interface TeachingQuickstartStudentCompetencySummary {
  studentId: string;
  quickstartBundleCount: number;
  quickstartEvidenceCount: number;
  totalEvidenceCount: number;
  competencyIds: number[];
}

export async function fetchTeachingUnits(params?: {
  status?: TeachingUnitSummary['status'];
  parentType?: TeachingUnitSummary['parent_type'];
  inTrash?: boolean;
}): Promise<TeachingUnitSummary[]> {
  const response = await apiClient.get('/unterrichten', { params });
  return response.data?.data ?? [];
}

export async function fetchTeachingUnitDetail(id: number): Promise<TeachingUnitDetail> {
  const response = await apiClient.get(`/unterrichten/${id}`);
  return response.data?.data;
}

export async function createTeachingUnitApi(payload: TeachingUnitCreatePayload) {
  const response = await apiClient.post('/unterrichten', payload);
  return response.data?.data;
}

export async function updateTeachingUnitApi(id: number, payload: TeachingUnitUpdatePayload) {
  const response = await apiClient.patch(`/unterrichten/${id}`, payload);
  return response.data?.data;
}

export async function fetchTeachingMeta(): Promise<TeachingPreparationMeta> {
  const response = await apiClient.get('/unterrichten/meta');
  return response.data?.data;
}

export async function fetchTeachingQuickstartContext(params: {
  className: string;
  subjectId?: number;
  theme: string;
  /** Live-Unterricht: ohne Bildersuche und schwere Vorbereitungsdaten */
  light?: boolean;
}): Promise<TeachingQuickstartContext> {
  const response = await apiClient.get('/unterrichten/quickstart/context', {
    params: {
      className: params.className,
      theme: params.theme,
      ...(params.subjectId != null ? { subjectId: params.subjectId } : {}),
      ...(params.light ? { light: '1' } : {})
    }
  });
  const data = response.data?.data || {};
  return {
    ...data,
    students: Array.isArray(data.students) ? data.students : [],
    sitzplanSuggestions: Array.isArray(data.sitzplanSuggestions) ? data.sitzplanSuggestions : [],
    competencySuggestions: Array.isArray(data.competencySuggestions) ? data.competencySuggestions : [],
    allCurriculumCompetencies: Array.isArray(data.allCurriculumCompetencies) ? data.allCurriculumCompetencies : [],
    defaultSelectedCompetencyIds: Array.isArray(data.defaultSelectedCompetencyIds) ? data.defaultSelectedCompetencyIds : [],
    keywordHints: Array.isArray(data.keywordHints) ? data.keywordHints : [],
    imageQueryHints: Array.isArray(data.imageQueryHints) ? data.imageQueryHints : [],
    imageGallery: Array.isArray(data.imageGallery) ? data.imageGallery : [],
    imageSourceSettings: data.imageSourceSettings || {
      wikipedia: true,
      pixabay: false,
      pexels: false,
      unsplash: false,
      google: false
    },
    curriculumMatches: Array.isArray(data.curriculumMatches) ? data.curriculumMatches : [],
    contentFieldMatches: Array.isArray(data.contentFieldMatches) ? data.contentFieldMatches : []
  } as TeachingQuickstartContext;
}

export async function presentQuickstartDisplayText(payload: {
  pin: string;
  text: string;
  title?: string;
}) {
  return presentDisplayText(payload);
}

export async function fetchKeywordDetails(params: {
  keyword: string;
  theme?: string;
  className?: string;
  curriculum?: string;
}): Promise<string> {
  const response = await apiClient.get('/unterrichten/keyword-details', { params });
  return String(response.data?.data?.text || '');
}

export async function fetchImageSearch(
  q: string,
  theme?: string
): Promise<QuickstartImageGalleryGroup[]> {
  const params: Record<string, string> = { q };
  if (theme) params.theme = theme;
  const response = await apiClient.get('/unterrichten/image-search', { params });
  const data = response.data?.data;
  return Array.isArray(data?.gallery) ? data.gallery : [];
}

export async function addQuickstartCompetencyBundle(
  teachingUnitId: number,
  payload: {
    studentId: string;
    competencyIds: number[];
    displayToastsEnabled?: boolean;
    displayRankingEnabled?: boolean;
  }
) {
  const response = await apiClient.post(
    `/unterrichten/${teachingUnitId}/student-competencies/add-bundle`,
    payload
  );
  return response.data?.data;
}

export async function removeQuickstartCompetencyBundle(
  teachingUnitId: number,
  payload: { studentId: string; displayToastsEnabled?: boolean; displayRankingEnabled?: boolean }
) {
  const response = await apiClient.post(
    `/unterrichten/${teachingUnitId}/student-competencies/remove-last-bundle`,
    payload
  );
  return response.data?.data;
}

export async function fetchTeachingQuickstartState(teachingUnitId: number): Promise<TeachingQuickstartSavedState | null> {
  const response = await apiClient.get(`/unterrichten/${teachingUnitId}/quickstart-state`);
  const data = response.data?.data;
  if (!data) {
    return null;
  }
  return {
    selectedCompetencyIds: Array.isArray(data.selectedCompetencyIds) ? data.selectedCompetencyIds : [],
    selectedKeywordHint: data.selectedKeywordHint || null,
    bookingCounts: data.bookingCounts || {},
    keywordHints: Array.isArray(data.keywordHints) ? data.keywordHints : [],
    plannedMediaItems: Array.isArray(data.plannedMediaItems) ? data.plannedMediaItems : []
  };
}

export async function saveTeachingQuickstartState(
  teachingUnitId: number,
  payload: TeachingQuickstartSavedState
): Promise<TeachingQuickstartSavedState> {
  const response = await apiClient.put(`/unterrichten/${teachingUnitId}/quickstart-state`, payload);
  return response.data?.data;
}

export async function moveTeachingUnitToTrash(id: number) {
  const response = await apiClient.post(`/unterrichten/${id}/move-to-trash`);
  return response.data?.data;
}

export async function restoreTeachingUnitFromTrash(id: number) {
  const response = await apiClient.post(`/unterrichten/${id}/restore-from-trash`);
  return response.data?.data;
}

export async function fetchTeachingQuickstartStudentCompetencySummary(
  teachingUnitId: number
): Promise<TeachingQuickstartStudentCompetencySummary[]> {
  const response = await apiClient.get(`/unterrichten/${teachingUnitId}/student-competencies/summary`);
  return response.data?.data ?? [];
}

export async function connectQuickstartDisplay(payload: {
  pin: string;
  teachingUnitId?: number | null;
  className?: string;
  theme?: string;
}) {
  return connectDisplay({
    pin: payload.pin,
    teachingUnitId: payload.teachingUnitId ?? null,
    context: {
      source: 'unterrichten-quickstart',
      className: payload.className || '',
      theme: payload.theme || ''
    }
  });
}

export async function disconnectQuickstartDisplay(pin: string) {
  return disconnectDisplay(pin);
}

export async function fetchQuickstartDisplayConnection() {
  return fetchMyDisplayConnection();
}

export async function fetchQuickstartDisplayStatus(pin: string) {
  return fetchDisplayStatus(pin);
}

export async function presentQuickstartDisplayImage(payload: {
  pin: string;
  imageUrl: string;
  title?: string;
  sourcePageUrl?: string;
}) {
  return presentDisplayImage(payload);
}

export async function presentQuickstartDisplayMedia(payload: {
  pin: string;
  assetId?: number | null;
  mediaUrl: string;
  mediaType?: 'image' | 'video' | 'pdf' | 'youtube';
  mimeType?: string;
  title?: string;
  sourcePageUrl?: string;
}) {
  return presentDisplayMedia(payload);
}

export async function uploadTeachingMediaFile(file: File, title?: string, teachingUnitId?: number | null) {
  const formData = new FormData();
  formData.append('file', file);
  if (title) {
    formData.append('title', title);
  }
  if (teachingUnitId) {
    formData.append('teachingUnitId', String(teachingUnitId));
  }
  const response = await apiClient.post('/unterrichten/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data?.data;
}
